import { useEffect, useState } from 'react';
import { FileText, User, Calendar } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import AdminLayout from '../components/AdminLayout';

const AdminAuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [expandedLog, setExpandedLog] = useState(null);

  useEffect(() => {
    fetchLogs();
  }, [currentPage, itemsPerPage]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const skip = (currentPage - 1) * itemsPerPage;
      const response = await api.get(`/admin/audit-logs?limit=${itemsPerPage}&skip=${skip}`);
      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'create_user': return 'bg-green-100 text-green-700';
      case 'delete_user': return 'bg-red-100 text-red-700';
      case 'edit_user': return 'bg-blue-100 text-blue-700';
      case 'activate_user': return 'bg-green-100 text-green-700';
      case 'deactivate_user': return 'bg-orange-100 text-orange-700';
      case 'assign_sheet': return 'bg-purple-100 text-purple-700';
      case 'sync_data': return 'bg-indigo-100 text-indigo-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getActionLabel = (action) => {
    return action.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  if (loading) {
    return <div className="text-center py-12">Loading audit logs...</div>;
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Audit Logs</h1>
          <p className="text-slate-600 mt-1">Track all admin actions and changes</p>
        </div>
        <div className="text-sm text-slate-600">
          Total Logs: <span className="font-semibold">{total}</span>
        </div>
      </div>

      {/* Logs List */}
      <div className="bg-white rounded-xl shadow-sm">
        {logs.length > 0 ? (
          <div className="divide-y divide-slate-100">
            {logs.map((log) => (
              <div key={log.id} className="p-6 hover:bg-slate-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    <div className={`p-3 rounded-lg ${getActionColor(log.action)}`}>
                      <FileText className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getActionColor(log.action)}`}>
                          {getActionLabel(log.action)}
                        </span>
                      </div>
                      <p className="text-slate-900 font-medium mb-1">
                        {log.details.split('\n')[0]}
                      </p>
                      
                      {/* Show expandable details if multi-line */}
                      {log.details.includes('\n') && (
                        <div className="mt-2">
                          <button
                            onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                          >
                            {expandedLog === log.id ? '▼ Hide Details' : '▶ Show Details'}
                          </button>
                          
                          {expandedLog === log.id && (
                            <div className="mt-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
                              <pre className="text-sm text-slate-700 whitespace-pre-wrap font-mono">
                                {log.details}
                              </pre>
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div className="flex items-center space-x-4 text-sm text-slate-600 mt-2">
                        <span className="flex items-center space-x-1">
                          <User className="w-4 h-4" />
                          <span>By: {log.admin_email}</span>
                        </span>
                        {log.target_user_email && (
                          <span className="flex items-center space-x-1">
                            <User className="w-4 h-4" />
                            <span>Target: {log.target_user_email}</span>
                          </span>
                        )}
                        <span className="flex items-center space-x-1">
                          <Calendar className="w-4 h-4" />
                          <span>
                            {format(new Date(log.timestamp), 'MMM d, yyyy h:mm a')}
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-12 text-center text-slate-500">
            <FileText className="w-12 h-12 mx-auto mb-4 opacity-20" />
            <p className="text-lg font-medium">No audit logs yet</p>
            <p className="text-sm mt-1">Admin actions will be logged here</p>
          </div>
        )}
        
        {/* Pagination Controls */}
        {logs.length > 0 && (
          <div className="flex items-center justify-between px-6 py-4 border-t">
            <div className="text-sm text-slate-600">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, total)} of {total} logs
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm text-slate-600">
                Page {currentPage} of {Math.ceil(total / itemsPerPage)}
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(Math.ceil(total / itemsPerPage), currentPage + 1))}
                disabled={currentPage >= Math.ceil(total / itemsPerPage)}
                className="px-3 py-1 border rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-2 py-1 border rounded text-sm"
              >
                <option value="10">10 per page</option>
                <option value="25">25 per page</option>
                <option value="50">50 per page</option>
                <option value="100">100 per page</option>
              </select>
            </div>
          </div>
        )}
      </div>
      </div>
    </AdminLayout>
  );
};

export default AdminAuditLogs;
